import React from "react";
import { Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import Header from "../Header";
import Footer from "../Footer";
import Seo from "../Seo";
import { Toaster } from "../ui/toaster";
import { ValuationProvider } from "../ValuationDialog";
import { Label } from "../ui/label";

export const SEL = "h-11 w-full rounded-md border border-[#e5ddd0] bg-white px-3 text-sm text-[#16323f] focus:border-[#35a4a4] focus:outline-none";

export const VALUATION_TYPES = [
  {
    key: "in-person", name: "In-Person Valuation", short: "In person", path: "/valuation/in-person",
    duration: "Around 45 mins", accuracy: "Most accurate",
    image: "https://static.prod-images.emergentagent.com/jobs/08e66633-9e16-4dc1-9465-ac033f74847a/images/5e624243c496cfa2c5b9cec151bf89ede88d625cc7aa3e708dbfeed03df1e960.jpeg",
    tagline: "You want to let in the next three months.",
    paras: [
      "Richard or George visits personally — assessing condition, presentation and current tenant demand in your postcode, and talking you through how to maximise your rent.",
      "You get a written, evidence-based figure backed by comparable lets nearby, plus an honest pricing strategy and a launch plan if you decide to instruct us.",
    ],
  },
  {
    key: "virtual", name: "Virtual Valuation", short: "Virtual", path: "/valuation/virtual",
    duration: "About 10 mins to submit", accuracy: "Around 85%",
    image: "https://static.prod-images.emergentagent.com/jobs/08e66633-9e16-4dc1-9465-ac033f74847a/images/4450b3c9e68362582e4b46065d67f195920aa4200b58aafbe4aef623273d71f6.jpeg",
    tagline: "You're planning to let within the year.",
    paras: [
      "Snap a few photos on your phone, tell us about the property and any improvements you've made, and send it over — no appointment needed.",
      "One of our senior team reviews everything against recent lets on your street and emails you a written rental figure, usually within one working day.",
    ],
  },
  {
    key: "instant", name: "Instant Valuation", short: "Instant", path: "/valuation/instant",
    duration: "Under a minute", accuracy: "Around 60%",
    image: "https://static.prod-images.emergentagent.com/jobs/08e66633-9e16-4dc1-9465-ac033f74847a/images/761f7de83842035a860126046bc836a13d9618843aeabc28a85a5aee1bbb6050.jpeg",
    tagline: "You're just curious what it might achieve.",
    paras: [
      "Enter a few details and get an estimated monthly rent range straight away. The figure is generated automatically from official ONS rental statistics for your borough, our live Hackney listings and current market data.",
      "It can't see your property's unique character, so treat it as a guide — then book an in-person or virtual valuation for the real number.",
    ],
  },
];

export function Field({ label, children, required, className = "" }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#7a716a]">{label}{required && " *"}</Label>
      {children}
    </div>
  );
}

export function ValuationShell({ type, title, description, children }) {
  const t = VALUATION_TYPES.find((x) => x.key === type);
  return (
    <ValuationProvider>
      <div className="App">
        <Seo title={title} description={description} path={t.path} />
        <Header />
        <section className="relative overflow-hidden bg-[#16323f] pt-32 pb-14 lg:pt-40" data-testid={`valuation-${type}-hero`}>
          <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-[#35a4a4]/10 blur-3xl" />
          <div className="relative mx-auto max-w-[1200px] px-5">
            <Link to="/valuation" data-testid="back-to-valuation-types" className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#d8b074] transition-colors hover:text-white">
              <ChevronLeft size={14} /> All valuation options
            </Link>
            <h1 className="mt-5 max-w-3xl font-display text-4xl font-700 uppercase leading-tight tracking-wide text-white lg:text-5xl">{t.name}</h1>
            <div className="mt-5 flex flex-wrap gap-x-8 gap-y-2 text-sm text-white/80">
              <span><span className="text-white/50">Duration</span> &nbsp;{t.duration}</span>
              <span><span className="text-white/50">Accuracy</span> &nbsp;{t.accuracy}</span>
            </div>
          </div>
        </section>
        <section className="bg-[#faf7f0] py-14 lg:py-20">
          <div className="mx-auto max-w-[1200px] px-5">{children}</div>
        </section>
        <Footer />
        <Toaster />
      </div>
    </ValuationProvider>
  );
}
